/** Root component — provider stack + router. */
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LicenseInfo } from '@mui/x-license';
import { RouterProvider } from 'react-router-dom';

import { queryClient } from '@/api/queryClient';
import { config } from '@/config';
import { router } from '@/routes';
import { theme } from '@/theme';

if (config.muiXLicenseKey) {
  LicenseInfo.setLicenseKey(config.muiXLicenseKey);
}

/**
 * Provider order:
 * - QueryClientProvider + ThemeProvider live ABOVE the router (they don't
 *   depend on router context).
 * - ErrorBoundary, SnackbarProvider, AuthProvider live INSIDE the router via a
 *   `RootLayout` route element — so any of them can use router hooks like
 *   `useNavigate()` (AuthProvider needs this to redirect on 401 / logout).
 */
export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <RouterProvider router={router} />
        {config.sentryEnv !== 'production' && <ReactQueryDevtools initialIsOpen={false} />}
      </ThemeProvider>
    </QueryClientProvider>
  );
}
